// ============================================================================
// OBT F2-B — Publication detayı: immutable snapshot + Formen park listesi
// ============================================================================
// Snapshot (GET /publications/{id}/snapshot) IMMUTABLE kaynaktır; cevap formu
// yalnız bu sorulardan üretilir. Parklar (my-parks) yalnız aktörün asli
// sorumluluğundadır; her park kendi cevap durumuyla eşleştirilir.
// ============================================================================

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  RefreshCw,
  MapPin,
  Camera,
  ChevronRight,
  PlusCircle,
  ListChecks,
  Eye,
} from 'lucide-react';
import {
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatusBadge,
} from '../ObtStates';
import { RESPONSE_STATUS_LABELS } from '../obtEnums';
import { usePublicationWorkspace } from './usePublicationWorkspace';
import { responseStatusLabel, questionTypeLabel } from './responseEnums';
import { parkFeatureLabelSafe } from './parkFeatures';
import SuggestedParkDialog from './SuggestedParkDialog';
import PreviewSubmitDialog from './PreviewSubmitDialog';

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
}

function ParkRow({ park, onOpenPark }) {
  const status = park.response_status;
  const isSupplemental = park.scope_source === 'approved_suggestion';
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-900">{park.park_name}</span>
          {isSupplemental && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
              Ek kapsam
            </span>
          )}
        </div>
        <p className="mt-0.5 pl-6 text-xs text-gray-500">
          {park.region_name ? park.region_name : 'Bölge belirtilmemiş'}
          {park.answer_count ? ` · ${park.answer_count} cevap` : ''}
          {park.photo_count ? ` · ${park.photo_count} foto` : ''}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-3 pl-6 sm:pl-0">
        {status ? (
          <ObtStatusBadge value={status} labelMap={RESPONSE_STATUS_LABELS} />
        ) : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
            {responseStatusLabel(null)}
          </span>
        )}
        <Button variant="outline" size="sm" onClick={() => onOpenPark(park)}>
          {status === 'revision_requested' ? 'Düzelt' : status ? 'Görüntüle' : 'Cevapla'}
          <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {string} props.publicationId
 * @param {boolean} props.enabled
 * @param {(park: object) => void} props.onOpenPark
 * @param {() => void} props.onSuggest
 * @param {() => void} props.onBack
 */
export default function PublicationDetail({
  publicationId,
  enabled = true,
  onOpenPark,
  onBack,
}) {
  const { loading, error, snapshot, parks, questions, reload } =
    usePublicationWorkspace(publicationId, { enabled });
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Model A: gönderilmeye hazır taslak sayısı (park başına 1 cevap).
  const draftCount = useMemo(
    () => (parks || []).filter((p) => p.response_status === 'draft').length,
    [parks]
  );

  const featureKeys = useMemo(() => {
    const keys = (questions || [])
      .map((q) => q.park_feature_key)
      .filter(Boolean);
    return Array.from(new Set(keys));
  }, [questions]);

  // Kampanya kapsamındaki park_id'ler (öneri diyaloğunda hariç tutulur).
  const scopedParkIds = useMemo(
    () => new Set((parks || []).map((p) => String(p.park_id))),
    [parks]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Görev listesi
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Yenile
          </Button>
          {/* Model A: doldurulan cevapları bir arada gözden geçir → topluca gönder. */}
          <Button size="sm" onClick={() => setPreviewOpen(true)} disabled={loading}>
            <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
            Önizle ve Gönder
            {draftCount > 0 && (
              <span className="ml-1.5 rounded-full bg-white/25 px-1.5 text-xs font-semibold">
                {draftCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {loading && <ObtLoading label="Bilgi talebi yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={reload} />}

      {!loading && !error && snapshot && (
        <>
          {/* Immutable snapshot başlığı */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{snapshot.campaign_title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {snapshot.description && (
                <p className="text-gray-700">{snapshot.description}</p>
              )}
              <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                {snapshot.topic && <span>Konu: {snapshot.topic}</span>}
                <span>
                  Yayınlayan: {snapshot.published_by?.name || 'Bilinmiyor'}
                </span>
                <span>Yayın: {formatDateTime(snapshot.published_at) || '—'}</span>
                <span>
                  Son cevap: {formatDateTime(snapshot.response_due_at) || 'Süresiz'}
                  {snapshot.due_passed ? ' (süresi geçti)' : ''}
                </span>
              </div>
              {featureKeys.length > 0 && (
                <p className="text-xs text-gray-600">
                  Park özelliği filtresi:{' '}
                  <span className="font-medium text-gray-800">
                    {featureKeys.map(parkFeatureLabelSafe).join(', ')}
                  </span>
                </p>
              )}
              <details className="pt-1">
                <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-gray-600">
                  <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
                  Soru seti ({questions.length})
                </summary>
                <ol className="mt-2 space-y-1 pl-4 text-xs text-gray-600">
                  {questions.map((q) => (
                    <li key={q.question_id} className="list-decimal">
                      <span className="font-medium text-gray-800">{q.question_text}</span>{' '}
                      <span className="text-gray-500">
                        ({questionTypeLabel(q.question_type)}
                        {q.is_required ? ', zorunlu' : ''}
                        {q.photo_required ? ', fotoğraf zorunlu' : ''})
                      </span>
                    </li>
                  ))}
                </ol>
              </details>
            </CardContent>
          </Card>

          {/* Park listesi */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Sorumlu Olduğunuz Parklar</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setSuggestOpen(true)}>
                <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" />
                Filtre dışı park öner
              </Button>
            </CardHeader>
            <CardContent>
              {parks.length === 0 ? (
                <ObtEmptyState
                  title="Bu bilgi talebinde size ait park yok"
                  description="Sahada ilgili özelliğe sahip bir park biliyorsanız 'Filtre dışı park öner' ile bildirebilirsiniz."
                  icon={Camera}
                />
              ) : (
                <ul className="space-y-2">
                  {parks.map((p) => (
                    <ParkRow
                      key={p.publication_park_id || p.supplemental_park_id || p.park_id}
                      park={p}
                      onOpenPark={(park) =>
                        onOpenPark(park, questions, {
                          // TAZE snapshot'tan son tarih + süre-doldu bayrağı. Liste
                          // öğesindeki değer BAYAT olabilir (Müdür sonradan süreyi
                          // uzatmışsa); park formu bunu değil bunu kullanmalı.
                          dueAt: snapshot.response_due_at,
                          duePassed: snapshot.due_passed,
                        })
                      }
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <SuggestedParkDialog
            publicationId={publicationId}
            scopedParkIds={scopedParkIds}
            open={suggestOpen}
            onOpenChange={setSuggestOpen}
            onSuccess={reload}
          />

          <PreviewSubmitDialog
            open={previewOpen}
            publicationId={publicationId}
            parks={parks}
            questions={questions}
            onClose={() => setPreviewOpen(false)}
            onEditPark={(park) =>
              onOpenPark(park, questions, {
                dueAt: snapshot.response_due_at,
                duePassed: snapshot.due_passed,
              })
            }
            onSubmitted={reload}
          />
        </>
      )}
    </div>
  );
}
